import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MdArrowBack, MdOutlineEventAvailable } from "react-icons/md";

import EndUserPublicLayout from "../../atoms/EndUserPublicLayout";
import { AlertMessage } from "../../atoms/AlertMessage";
import { EmptyState } from "../../atoms/EmptyState";
import { Spinner } from "../../atoms/Spinner";
import { CustomButton } from "../../atoms/CustomButton";
import { ModalShell } from "../../atoms/ModalShell";
import {
  BookedAppointment,
  BookingClientProof,
  BookingSlot,
  bookingRefusalOf,
  useBookPublicSlot,
  usePublicBookingPage,
  usePublicBookingSlots,
  usePublicBookingType,
} from "../../../hooks/agenda/publicBooking";
import {
  BookingSlotPicker,
  useBookingMoment,
} from "../customRegister/components/BookingSlotPicker";
import { BookingConfirmation } from "../customRegister/components/BookingConfirmation";
import {
  BookingTypeDescription,
  BookingTypeImage,
  BookingTypeLocation,
} from "../customRegister/components/BookingTypeDetails";
import { durationLabel } from "../dashboard/agenda/agendaDates";
import { formatPrice } from "../dashboard/agenda/agendaPayment";
import { FROM_BOOKING_PAGE } from "./BookingPage";
import { bookingPagePath } from "../../atoms/BookingLinkButton";
import {
  forgetAddressSecrets,
  searchWithSecrets,
} from "../../../utils/addressSecrets";
import {
  captureBookingUtmParams,
  withUtmParams,
} from "../customRegister/utmCapture";
import {
  BookingPaymentReturn,
  BookingPaymentState,
  PaymentReturn,
} from "./BookingPayment";
import {
  FormFinishAfterBooking,
  FormFinishButton,
  FormFinishDone,
  FormFinishLeaving,
  useFormRegistrationEnd,
} from "./BookingFormFinish";

type Notice = { tone: "warning" | "failure"; message: string };

type Session = { loginToken: string; userId: string };

// "member" is the person a business card offers; "chosen" is the one picked here before logging in.
const RETURN_PARAMS = [
  "login_token",
  "user_identification",
  "last_connection",
  "user_is_active",
  "chosen",
  "start",
  "appointment",
  "user",
  "sig",
  "from_form",
];

const FORM_ID = /^([a-f0-9]{4,}|default)$/;

const BOOKING_HOLD_MINUTES = 15;

type Choice = { member?: string; start?: number };

const choiceKey = (entityId: string, typeId: string) =>
  `sum:booking-choice:${entityId}:${typeId}`;

/** The time picked before leaving to log in or sign up, kept for the tab so that coming back finds it. */
const choiceOf = (entityId: string, typeId: string): Choice => {
  try {
    const kept = JSON.parse(
      sessionStorage.getItem(choiceKey(entityId, typeId)) ?? "null"
    );
    if (typeof kept?.member === "string" && Number.isFinite(kept?.start))
      return { member: kept.member, start: kept.start };
  } catch {}
  return {};
};

const rememberChoice = (entityId: string, typeId: string, choice: Choice) => {
  try {
    sessionStorage.setItem(choiceKey(entityId, typeId), JSON.stringify(choice));
  } catch {}
};

const forgetChoice = (entityId: string, typeId: string) => {
  try {
    sessionStorage.removeItem(choiceKey(entityId, typeId));
  } catch {}
};

/**
 * What a reload of the tunnel takes up again: the client's login, the form that sent them and their payment return or
 * booking. Kept on the tunnel's entry of the tab's history, which a later visit of the type does not have.
 */
type KeptArrival = {
  session: Session | null;
  fromForm: string | null;
  paymentReturn: PaymentReturn | null;
  /** Whether the payment return still shows, the client not having picked another time. */
  returnShown: boolean;
  appointment: BookedAppointment | null;
};

const KEPT_ARRIVAL = "bookingArrival";

const keptArrivalOf = (state: unknown): KeptArrival | null =>
  ((state as Record<string, unknown> | null)?.[KEPT_ARRIVAL] as
    | KeptArrival
    | undefined) ?? null;

/** A page the client leaves for, in the language they read this one in. */
const inLanguage = (href: string, language: string) => {
  const url = new URL(href, window.location.origin);
  url.searchParams.set("language", language);
  return /^https?:\/\//i.test(href)
    ? url.toString()
    : `${url.pathname}${url.search}${url.hash}`;
};

const readArrival = (search: string) => {
  const params = new URLSearchParams(search);
  const loginToken = params.get("login_token");
  const userId = params.get("user_identification");
  const offered = params.get("member") || undefined;
  const fromForm = params.get("from_form");
  const paid = {
    id: params.get("appointment"),
    userId: params.get("user"),
    sig: params.get("sig"),
  };
  return {
    paymentReturn:
      paid.id && paid.userId && paid.sig ? (paid as PaymentReturn) : null,
    offered,
    resumeMember: params.get("chosen") || offered,
    resumeStart: Number(params.get("start")) || undefined,
    session: loginToken && userId ? { loginToken, userId } : null,
    fromForm: fromForm && FORM_ID.test(fromForm) ? fromForm : null,
  };
};

const BookingTunnelPage: React.FC = () => {
  const { entityId, typeId } = useParams<{
    entityId: string;
    typeId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("clientView");
  const { lang } = useBookingMoment();

  const [arrival] = useState(() => {
    const fresh = readArrival(
      searchWithSecrets(location.pathname, location.search).toString()
    );
    const kept = keptArrivalOf(location.state);
    if (!kept || fresh.session || fresh.paymentReturn) {
      return { ...fresh, returnShown: true, appointment: null };
    }
    return {
      ...fresh,
      session: kept.session,
      fromForm: fresh.fromForm ?? kept.fromForm,
      paymentReturn: kept.paymentReturn,
      returnShown: kept.returnShown,
      appointment: kept.appointment,
    };
  });
  useEffect(() => {
    forgetAddressSecrets(location.pathname, RETURN_PARAMS);
    // Read once: a reload takes them up from the history entry, a later visit of this type starts without them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [utm] = useState(() =>
    captureBookingUtmParams(entityId ?? "", location.search)
  );
  const fromBookingPage = Boolean(
    (location.state as Record<string, unknown> | null)?.[FROM_BOOKING_PAGE]
  );
  const [session, setSession] = useState<Session | null>(arrival.session);
  const isSignedIn = session !== null;

  const typeQuery = usePublicBookingType(entityId, typeId);
  const entityPage = usePublicBookingPage(entityId, undefined, {
    enabled: typeQuery.isError,
  });
  const bookingType = typeQuery.data;
  const members = useMemo(
    () => bookingType?.members ?? [],
    [bookingType?.members]
  );
  const preselected = members.some(
    (m) => m.user_identification === arrival.offered
  )
    ? arrival.offered
    : undefined;

  const [kept] = useState<Choice>(() =>
    arrival.resumeStart ? {} : choiceOf(entityId ?? "", typeId ?? "")
  );
  const [member, setMember] = useState<string | undefined>(
    arrival.resumeMember ?? kept.member
  );
  const [start, setStart] = useState<number | undefined>(
    arrival.resumeStart ?? kept.start
  );
  const [accountFor, setAccountFor] = useState<BookingSlot | null>(null);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const heldUntil = useRef<number | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [sessionLost, setSessionLost] = useState(false);
  const [appointment, setAppointment] = useState<BookedAppointment | null>(
    arrival.appointment
  );
  const [paymentReturn, setPaymentReturn] = useState(
    arrival.returnShown ? arrival.paymentReturn : null
  );
  const [paying, setPaying] = useState(false);
  const showBooked = useCallback((booked: BookedAppointment) => {
    setPaymentReturn(null);
    setAppointment(booked);
  }, []);
  const [resuming, setResuming] = useState(false);
  const resumed = useRef(false);

  const returned = arrival.paymentReturn;
  const proof: BookingClientProof | null = session
    ? { loginToken: session.loginToken }
    : returned
    ? { bookingReturn: { id: returned.id, sig: returned.sig } }
    : null;
  const ending = useFormRegistrationEnd({
    entityId,
    formId: arrival.fromForm,
    userId: session?.userId ?? returned?.userId,
    proof,
    onSessionExpired: () => {
      setSession(null);
      setSessionLost(true);
      setNotice({
        tone: "warning",
        message: t("bookingTunnel.sessionExpired"),
      });
    },
  });
  const loginStillNeeded =
    ending.state.kind !== "done" && (!appointment || Boolean(arrival.fromForm));

  const keptArrival = useMemo<KeptArrival | null>(
    () =>
      session || arrival.fromForm || arrival.paymentReturn || appointment
        ? {
            session: loginStillNeeded ? session : null,
            fromForm: arrival.fromForm,
            paymentReturn: arrival.paymentReturn,
            returnShown: paymentReturn !== null,
            appointment,
          }
        : null,
    [session, loginStillNeeded, arrival, paymentReturn, appointment]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    RETURN_PARAMS.forEach((key) => params.delete(key));
    const query = params.toString();
    const search = query ? `?${query}` : "";
    const state = (location.state ?? {}) as Record<string, unknown>;
    if (
      search === location.search &&
      JSON.stringify(state[KEPT_ARRIVAL] ?? null) ===
        JSON.stringify(keptArrival)
    ) {
      return;
    }
    navigate(`${location.pathname}${search}`, {
      replace: true,
      state: { ...state, [KEPT_ARRIVAL]: keptArrival },
    });
  }, [
    keptArrival,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const finishWithout = ending.canFinish && (
    <FormFinishButton
      booked={false}
      onClick={ending.finish}
      isLoading={ending.isFinishing}
    />
  );

  const slotsQuery = usePublicBookingSlots({
    entityId,
    typeId,
    member,
    enabled: !!bookingType && !appointment && !paymentReturn,
  });
  const { mutate: book, isLoading: isBooking } = useBookPublicSlot(
    entityId,
    typeId
  );

  // Back from the payment page through the history, the page returns as it was left, its redirect message included.
  useEffect(() => {
    if (!paying) return;
    const backFromPayment = () => {
      setPaying(false);
      setStart(undefined);
      slotsQuery.refetch();
    };
    window.addEventListener("pageshow", backFromPayment);
    return () => window.removeEventListener("pageshow", backFromPayment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paying]);

  // A phone may freeze the timer in the background: coming back to the page checks the time as well.
  useEffect(() => {
    if (retryAt === null) return;
    const release = () => {
      if (Date.now() < retryAt) return;
      setRetryAt(null);
      setNotice(null);
      slotsQuery.refetch();
    };
    const id = window.setTimeout(release, retryAt - Date.now() + 500);
    window.addEventListener("pageshow", release);
    document.addEventListener("visibilitychange", release);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("pageshow", release);
      document.removeEventListener("visibilitychange", release);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAt]);

  useEffect(() => {
    if (!bookingType) return;
    const known = members.some((m) => m.user_identification === member);
    if (member && !known) setMember(undefined);
    else if (!member && members.length === 1)
      setMember(members[0].user_identification);
  }, [bookingType, members, member]);

  const memberName = (id?: string) =>
    members.find((m) => m.user_identification === id)?.name ||
    (t("customRegistration.page.booking.memberFallback") as string);

  const keepChoice = (slotStart: number) =>
    rememberChoice(entityId ?? "", typeId ?? "", {
      member,
      start: slotStart,
    });

  const redirectToLogin = (slot: BookingSlot) => {
    if (!bookingType || !member) return;
    keepChoice(slot.start);
    const back = new URLSearchParams(
      preselected ? { member: preselected } : { chosen: member }
    );
    back.set("start", String(slot.start));
    if (arrival.fromForm) back.set("from_form", arrival.fromForm);
    const tunnelPath = `/booking/${entityId}/${typeId}?${back}`;
    window.location.assign(
      `${bookingType.login_url}?redirect=${encodeURIComponent(
        tunnelPath
      )}&language=${lang}`
    );
  };

  const submit = (chosenMember: string, slotStart: number) => {
    if (!session) return;
    setNotice(null);
    book(
      {
        userId: session.userId,
        loginToken: session.loginToken,
        member: chosenMember,
        start: slotStart,
        lang,
        fromForm: arrival.fromForm,
      },
      {
        onSuccess: (result) => {
          forgetChoice(entityId ?? "", typeId ?? "");
          if (result.status !== "pending") {
            setAppointment(result);
          } else if (result.payment_link) {
            heldUntil.current = result.hold_expires_at ?? null;
            setPaying(true);
            window.location.assign(result.payment_link);
          } else {
            toast.error(t("customRegistration.page.toast.unexpectedError"));
          }
        },
        onSettled: () => setResuming(false),
        onError: (error) => {
          const reason = bookingRefusalOf(error);
          const status = error?.response?.status;
          setStart(undefined);
          if (status === 503) {
            setNotice({
              tone: "failure",
              message: t(
                bookingType?.type.price
                  ? "bookingTunnel.payment.unavailable"
                  : "bookingTunnel.serviceUnavailable"
              ),
            });
          } else if (status === 401) {
            setSession(null);
            setNotice({
              tone: "warning",
              message: t("bookingTunnel.sessionExpired"),
            });
          } else if (reason === "SLOT_TAKEN") {
            setNotice({
              tone: "warning",
              message: t("customRegistration.page.booking.slotTaken"),
            });
            slotsQuery.refetch();
          } else if (reason === "PAYMENT_UNAVAILABLE") {
            setNotice({
              tone: "failure",
              message: t("bookingTunnel.payment.unavailableForType"),
            });
            slotsQuery.refetch();
          } else if (reason === "TOO_MANY_UPCOMING") {
            const now = Date.now();
            const held = heldUntil.current;
            setRetryAt(
              held && held > now ? held : now + BOOKING_HOLD_MINUTES * 60_000
            );
            setNotice({
              tone: "failure",
              message: t("customRegistration.page.booking.tooManyUpcoming", {
                minutes: BOOKING_HOLD_MINUTES,
              }),
            });
          } else if (reason === "MEMBER_NOT_OFFERED") {
            setNotice({
              tone: "warning",
              message: t("bookingTunnel.memberUnavailable"),
            });
            setMember(undefined);
            typeQuery.refetch();
          } else if (reason) {
            setNotice({
              tone: "warning",
              message: t("customRegistration.page.booking.slotUnavailable"),
            });
            slotsQuery.refetch();
          } else if (status === 404) {
            setNotice({
              tone: "warning",
              message: t("customRegistration.page.booking.slotUnavailable"),
            });
            typeQuery.refetch();
            slotsQuery.refetch();
          } else if (status === 429) {
            setNotice({
              tone: "warning",
              message: t("customRegistration.page.toast.tooManyAttempts"),
            });
          } else if (status === 400) {
            setNotice({
              tone: "warning",
              message: t(
                chosenMember === session.userId
                  ? "bookingTunnel.cannotBookYourself"
                  : "bookingTunnel.bookingRefused"
              ),
            });
            slotsQuery.refetch();
          } else {
            // The booking may have been made before its answer was lost, so a retry could book it twice.
            setNotice({
              tone: "failure",
              message: t("bookingTunnel.bookingLost"),
            });
            slotsQuery.refetch();
          }
        },
      }
    );
  };

  useEffect(() => {
    if (resumed.current || !bookingType || !session) return;
    const { resumeMember, resumeStart } = arrival;
    if (!resumeMember || !resumeStart) return;
    const picked = choiceOf(entityId ?? "", typeId ?? "");
    if (picked.member !== resumeMember || picked.start !== resumeStart) return;
    resumed.current = true;
    forgetChoice(entityId ?? "", typeId ?? "");
    setResuming(true);
    submit(resumeMember, resumeStart);
    // Runs once, when the customer comes back from the login page in the tab that chose the slot: anyone can forge an address.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingType, session]);

  const goToSignup = (slot: BookingSlot) => {
    if (!bookingType?.signup_url) return;
    keepChoice(slot.start);
    window.location.assign(
      withUtmParams(inLanguage(bookingType.signup_url, lang), utm)
    );
  };

  const onConfirm = (slot: BookingSlot) => {
    if (!member) return;
    if (isSignedIn) submit(member, slot.start);
    else if (bookingType?.signup_url) setAccountFor(slot);
    else redirectToLogin(slot);
  };

  const chooseMember = (id: string) => {
    setMember(id);
    setStart(undefined);
    if (retryAt === null) setNotice(null);
  };

  if (typeQuery.isLoading && !appointment && !paymentReturn) {
    return (
      <EndUserPublicLayout>
        <div className="flex justify-center py-8" translate="no">
          <Spinner size="md" />
        </div>
      </EndUserPublicLayout>
    );
  }

  const entityOfPage = bookingType?.entity ?? entityPage.data?.entity;
  const layoutProps = {
    entityLogo: entityOfPage?.logo,
    entityName: entityOfPage?.name,
  };

  if (ending.state.kind === "leaving") {
    return (
      <EndUserPublicLayout {...layoutProps}>
        <FormFinishLeaving />
      </EndUserPublicLayout>
    );
  }

  if (ending.state.kind === "done" && !appointment) {
    return (
      <EndUserPublicLayout {...layoutProps}>
        <FormFinishDone message={ending.state.message} alone />
      </EndUserPublicLayout>
    );
  }

  if (paying) {
    return (
      <EndUserPublicLayout {...layoutProps}>
        <div
          className="flex items-center justify-center gap-2 py-8 text-sm text-gray-600"
          translate="no"
        >
          <Spinner size="sm" />
          {t("bookingTunnel.payment.redirecting")}
        </div>
      </EndUserPublicLayout>
    );
  }

  if (appointment) {
    const bookedWith = member ?? appointment.organizer;
    const withName =
      appointment.participants?.find(
        (participant) => participant.user_identification === bookedWith
      )?.name || memberName(bookedWith);
    return (
      <EndUserPublicLayout {...layoutProps}>
        <BookingConfirmation
          appointment={appointment}
          withName={withName}
          serviceTitle={appointment.title || bookingType?.type.title}
          calendarTitle={
            appointment.title ||
            bookingType?.type.title ||
            (t("customRegistration.page.booking.calendarTitle", {
              name: withName,
            }) as string)
          }
        >
          {appointment.payment && (
            <BookingPaymentState
              payment={appointment.payment}
              link={appointment.payment_link ?? appointment.payment.link}
            />
          )}
          {ending.state.kind === "done" ? (
            <FormFinishDone message={ending.state.message} />
          ) : sessionLost ? (
            <AlertMessage color="warning">
              {t("bookingTunnel.form.sessionExpired")}
            </AlertMessage>
          ) : (
            ending.canFinish && (
              <FormFinishAfterBooking
                onFinish={ending.finish}
                isFinishing={ending.isFinishing}
                automatic={
                  appointment.payment?.status !== "due" && !ending.cameBack
                }
              />
            )
          )}
        </BookingConfirmation>
      </EndUserPublicLayout>
    );
  }

  if (paymentReturn && entityId && typeId) {
    return (
      <EndUserPublicLayout {...layoutProps} title={bookingType?.type.title}>
        <BookingPaymentReturn
          entityId={entityId}
          typeId={typeId}
          paymentReturn={paymentReturn}
          onBooked={showBooked}
          onPickAnother={() => setPaymentReturn(null)}
          finishWithout={finishWithout}
        />
      </EndUserPublicLayout>
    );
  }

  if (typeQuery.isError && typeQuery.error?.response?.status !== 404) {
    return (
      <EndUserPublicLayout {...layoutProps}>
        <EmptyState
          icon={<MdOutlineEventAvailable size={24} />}
          title={t("bookingTunnel.serviceUnavailable")}
          action={
            <CustomButton
              variant="secondary"
              title={t("bookingTunnel.payment.retry") as string}
              isLoading={typeQuery.isFetching}
              onClick={() => typeQuery.refetch()}
            />
          }
        />
      </EndUserPublicLayout>
    );
  }

  if (typeQuery.isError || !bookingType) {
    return (
      <EndUserPublicLayout {...layoutProps}>
        <EmptyState
          icon={<MdOutlineEventAvailable size={24} />}
          title={t("bookingTunnel.unavailableTitle")}
          description={
            ending.canFinish
              ? t("customRegistration.page.toast.bookingUnavailable")
              : t("bookingTunnel.unavailableDescription")
          }
          action={
            ending.canFinish && (
              // Nothing left to book here: the registration only has to end, as after a booking.
              <FormFinishButton
                booked
                onClick={ending.finish}
                isLoading={ending.isFinishing}
              />
            )
          }
        />
      </EndUserPublicLayout>
    );
  }

  const duration = durationLabel(
    t,
    "bookingTunnel.duration",
    bookingType.type.duration_min
  );
  const price = bookingType.type.price
    ? formatPrice(
        bookingType.type.price.amount,
        bookingType.type.price.currency,
        lang
      )
    : null;
  const subtitle = [
    duration,
    price,
    preselected ? memberName(preselected) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <EndUserPublicLayout
      {...layoutProps}
      title={bookingType.type.title}
      subtitle={subtitle}
      media={
        <>
          {fromBookingPage && (
            <Link
              to={bookingPagePath(entityId ?? "", arrival.offered)}
              className="inline-flex items-center gap-1 self-start rounded-md text-sm font-medium text-blue-600 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <MdArrowBack
                size={16}
                className="rtl:rotate-180"
                aria-hidden="true"
              />
              {t("bookingPage.allServices")}
            </Link>
          )}
          <BookingTypeImage
            src={bookingType.type.image_url}
            alt={bookingType.type.title}
          />
        </>
      }
    >
      <BookingTypeLocation location={bookingType.type.location} />
      <BookingTypeDescription html={bookingType.type.description} />
      {members.length === 0 ? (
        <div className="space-y-6">
          <EmptyState
            bordered
            icon={<MdOutlineEventAvailable size={24} />}
            title={t("customRegistration.page.booking.unavailableTitle")}
            description={t("bookingTunnel.noMembers")}
          />
          {finishWithout}
        </div>
      ) : resuming ? (
        <div
          className="flex items-center justify-center gap-2 py-8 text-sm text-gray-600"
          translate="no"
        >
          <Spinner size="sm" />
          {t("bookingTunnel.booking")}
        </div>
      ) : (
        <div className="space-y-6">
          <BookingSlotPicker
            members={members}
            member={member}
            onMemberChange={chooseMember}
            showMemberChoice={!preselected}
            slots={slotsQuery.data ?? []}
            isLoadingSlots={slotsQuery.isLoading}
            isSlotsError={slotsQuery.isError}
            start={start}
            onStartChange={setStart}
            onConfirm={onConfirm}
            isConfirming={isBooking}
            slotsDisabled={retryAt !== null}
            confirmNote={
              price && (
                <p className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-gray-600">
                    {t("bookingTunnel.payment.price")}
                  </span>
                  <span className="font-semibold tabular-nums text-gray-900">
                    {price}
                  </span>
                </p>
              )
            }
            notice={
              notice && (
                <AlertMessage color={notice.tone}>
                  {notice.message}
                </AlertMessage>
              )
            }
            memberName={memberName}
          />

          {finishWithout}

          {!isSignedIn && (
            <div className="space-y-1 border-t border-gray-200 pt-4 text-center text-sm">
              <p className="text-gray-600">{t("bookingTunnel.loginHint")}</p>
              {bookingType.signup_url ? (
                <a
                  href={withUtmParams(
                    inLanguage(bookingType.signup_url, lang),
                    utm
                  )}
                  onClick={() => start && keepChoice(start)}
                  className="font-medium text-blue-600 hover:text-blue-800"
                >
                  {t("bookingTunnel.noAccount")}
                </a>
              ) : (
                <p className="text-gray-500">
                  {t("bookingTunnel.registeredOnly")}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      <ModalShell
        show={accountFor !== null}
        onClose={() => setAccountFor(null)}
        title={t("bookingTunnel.account.title")}
        size="md"
        dismissible
      >
        <p className="text-sm text-gray-600">
          {t("bookingTunnel.account.text")}
        </p>
        <div className="mt-4 flex flex-col gap-3">
          <CustomButton
            variant="primary"
            className="w-full"
            title={t("bookingTunnel.account.haveAccount") as string}
            onClick={() => accountFor && redirectToLogin(accountFor)}
          />
          <CustomButton
            variant="secondary"
            className="w-full"
            title={t("bookingTunnel.noAccount") as string}
            onClick={() => accountFor && goToSignup(accountFor)}
          />
        </div>
      </ModalShell>
    </EndUserPublicLayout>
  );
};

export default BookingTunnelPage;
